import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch } from 'react';
import { toast } from 'sonner';
import type { Session } from '@obraplan/core/application/session';
import type { FloorPlanSheetView } from '@obraplan/core/application/use-cases/takeoff/floor-plan-sheet-use-cases';
import type { ProjectTakeoff } from '@obraplan/core/application/use-cases/takeoff/takeoff-use-cases';
import {
  FLOOR_PLAN_SHEET_ERROR,
  MAX_PLAN_SHEET_BYTES,
  metresPerPixelAt,
} from '@obraplan/core/domain/takeoff/floor-plan-sheet';
import type { ProjectId } from '@obraplan/core/domain/shared/ids';
import { ERROR_CODE } from '@obraplan/core/application/errors/error-codes';
import type { Container } from '@/app/container';
import { formatBytes } from '@/lib/format';
import {
  alvoDaPranchaAntiga,
  bytesDoDataUrl,
  desenhoDaTipologia,
  tipologiasDoDesenho,
} from './prancha-do-pavimento';
import { paginaParaGuardar, renderizarPrancha, type Renderizador } from './renderizar-prancha';
import {
  activeTypology,
  type PlanDraft,
  type WorkspaceAction,
  type WorkspaceDraft,
} from './typology-draft';
import { esquecerPranchaAntiga, lerPranchaAntiga } from './use-workspace-draft';

/**
 * A prancha da mesa, vinda do servidor (Fase 24, D23).
 *
 * A bancada continua tendo **um** lugar para a prancha (`workspace.plan`), e é nele que a
 * planta desenha. O que mudou é quem o preenche: antes, o `localStorage`; agora, este
 * hook, com a prancha do **desenho de pavimento** em que a tipologia da mesa está.
 * Trocar para uma tipologia de outro desenho troca a folha — que é o que a prancha por
 * pavimento quer dizer.
 *
 * Três leituras, cada uma no seu momento:
 *
 * - **o registro** das pranchas da obra, ao abrir a tela: é pouco, e diz quem anexou;
 * - **o arquivo**, só quando a planta é aberta (`abrir`): são megabytes, e quem veio à
 *   tela ver a consolidação não precisa deles;
 * - **a prancha de antes**, uma vez: a que o navegador guardava sobe para o desenho em que
 *   ela foi traçada (`alvoDaPranchaAntiga`) — a menos que o servidor já tenha uma.
 *
 * As escritas atualizam o registro na mão, sem reler: quem gravou já tem a resposta. A
 * releitura acontece a cada `abrir`, e é ela que mostra a prancha que outra pessoa trocou
 * enquanto a tela estava aberta — no momento em que isso importa, antes de alguém ler
 * "anexada por" e decidir.
 */

export type CasosDaPrancha = Pick<
  Container,
  | 'getProjectFloorPlanSheets'
  | 'getFloorPlanSheetFile'
  | 'attachFloorPlanSheet'
  | 'calibrateFloorPlanSheet'
  | 'removeFloorPlanSheet'
>;

/** O que a planta mostra depois de pedir: erro no lugar do erro, aviso quando houver. */
export type ResultadoDaPrancha =
  { readonly ok: true; readonly aviso?: string } | { readonly ok: false; readonly erro: string };

export interface PranchaDaMesa {
  /** O desenho de pavimento da tipologia da mesa. Ausente: ela não está em nenhum. */
  readonly desenhoId?: string;
  /** A prancha guardada para esse desenho, com quem a anexou. */
  readonly anexada?: FloorPlanSheetView;
  /** O registro ainda não chegou — não se sabe se o desenho tem prancha. */
  readonly carregando: boolean;
  /** O arquivo está vindo do servidor, ou sendo renderizado. */
  readonly abrindo: boolean;
  /** Um arquivo está subindo. */
  readonly enviando: boolean;
  /** O que impediu a prancha de chegar à mesa — dito na planta, e não engolido. */
  readonly falha?: string;
  /** A planta foi aberta: é agora que o arquivo vale o que custa. */
  readonly abrir: () => void;
  /** Anexa, ou troca a que está lá. A confirmação é da tela, antes de chamar. */
  readonly anexar: (arquivo: File) => Promise<ResultadoDaPrancha>;
  readonly remover: () => Promise<ResultadoDaPrancha>;
  readonly calibrar: (metresPerPixel: number) => Promise<ResultadoDaPrancha>;
}

export interface EntradaDaPrancha {
  readonly container: CasosDaPrancha;
  readonly session: Session | undefined;
  readonly projectId: ProjectId | undefined;
  /** Os desenhos e as posições da obra — de onde sai o desenho de cada tipologia. */
  readonly espinha: Pick<ProjectTakeoff, 'floorPlans' | 'positions'> | undefined;
  readonly workspace: WorkspaceDraft;
  readonly dispatch: Dispatch<WorkspaceAction>;
  /** Injetável: o `canvas` e o `pdfjs` não existem fora do navegador. */
  readonly renderizar?: Renderizador;
  /** Injetável pelo mesmo motivo: a página como arquivo para guardar (RN-248). */
  readonly guardarPagina?: typeof paginaParaGuardar;
}

/** O servidor ou o caminho até ele recusaram pelo tamanho — há o que fazer (RN-248). */
function grandeDemais(code: string): boolean {
  return code === ERROR_CODE.REQUEST_TOO_LARGE || code === FLOOR_PLAN_SHEET_ERROR.FILE_TOO_LARGE;
}

/** O aviso de quando a página renderizada sobe no lugar do original (RN-248). */
function avisoDaRenderizada(tamanho: number, motivo: 'limite' | 'caminho'): string {
  const porque =
    motivo === 'limite'
      ? `O arquivo tem ${formatBytes(tamanho)}, e o limite para guardar o original é ${formatBytes(MAX_PLAN_SHEET_BYTES)}.`
      : `O caminho até o servidor recusou o arquivo de ${formatBytes(tamanho)}.`;
  return `${porque} Guardei a página renderizada no lugar: o traço e a escala ficam iguais — só o zoom máximo perde a nitidez do vetor.`;
}

export function usePranchaDoPavimento(entrada: EntradaDaPrancha): PranchaDaMesa {
  const { container, session, projectId, espinha, workspace, dispatch } = entrada;
  const renderizar = entrada.renderizar ?? renderizarPrancha;
  const guardarPagina = entrada.guardarPagina ?? paginaParaGuardar;

  const [pranchas, setPranchas] = useState<readonly FloorPlanSheetView[] | undefined>(undefined);
  const [versao, setVersao] = useState(0);
  const [pedida, setPedida] = useState(false);
  const [abrindo, setAbrindo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [falha, setFalha] = useState<string | undefined>(undefined);

  /** A prancha que está em `workspace.plan`: o id, `null` para nenhuma, `undefined` no início. */
  const naMesa = useRef<string | null | undefined>(undefined);
  /** A página já renderizada de cada prancha: trocar de tipologia e voltar não refaz o PDF. */
  const renderizadas = useRef(new Map<string, PlanDraft>());
  /** Quem lê o rascunho mais novo sem entrar nas dependências dos efeitos. */
  const bancada = useRef(workspace);
  bancada.current = workspace;
  const migracaoFeita = useRef(false);
  /**
   * A prancha de antes, lida uma vez. `null` é "ainda não li": o efeito da migração volta
   * a cada mudança do desenho enquanto espera o traçado chegar, e reler megabytes de JSON
   * do `localStorage` a cada tecla seria pagar a espera caro.
   */
  const antiga = useRef<PlanDraft | undefined | null>(null);

  const unitTypeId = activeTypology(workspace).unitTypeId;
  const desenhoId = useMemo(
    () => (espinha === undefined ? undefined : desenhoDaTipologia(espinha.positions, unitTypeId)),
    [espinha, unitTypeId],
  );
  const anexada = useMemo(
    () =>
      desenhoId === undefined
        ? undefined
        : pranchas?.find((prancha) => prancha.floorPlanId === desenhoId),
    [pranchas, desenhoId],
  );

  // ── O registro ──────────────────────────────────────────────────────────────
  const userId = session?.userId;
  useEffect(() => {
    if (projectId === undefined || session === undefined) return;
    let vivo = true;
    void container.getProjectFloorPlanSheets.execute(projectId, session).then(
      (resultado) => {
        if (!vivo) return;
        if (resultado.ok) {
          setPranchas(resultado.value);
          setFalha(undefined);
        } else {
          // Sem o registro, não se sabe se o desenho tem prancha. Continuar como se não
          // tivesse subiria a prancha antiga por cima de uma que existe.
          setFalha(`As pranchas da obra não carregaram: ${resultado.error.message}`);
        }
      },
      () => {
        if (vivo) setFalha('As pranchas da obra não carregaram — verifique a conexão.');
      },
    );
    return () => {
      vivo = false;
    };
    // `session` entra pelo usuário: o objeto muda de identidade sem mudar de dono.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container, projectId, userId, versao]);

  // ── A prancha da mesa ───────────────────────────────────────────────────────
  /** A prancha que deve estar na mesa agora — o arquivo que chegar tarde confere com ela. */
  const desejada = useRef<FloorPlanSheetView | undefined>(undefined);
  /** O arquivo que está vindo. Um só por prancha: são megabytes, e pedir duas vezes dobra. */
  const emVoo = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (pranchas === undefined || projectId === undefined || session === undefined) return;
    desejada.current = anexada;

    if (anexada === undefined) {
      // O desenho não tem prancha: a folha de outro desenho não pode ficar na mesa, senão
      // o traçado desta tipologia seria feito em cima do pavimento errado.
      if (naMesa.current !== null && bancada.current.plan !== undefined) {
        dispatch({ type: 'CLEAR_PLAN' });
      }
      naMesa.current = null;
      return;
    }
    if (!pedida || naMesa.current === anexada.id) return;

    const assentar = (id: string, pagina: PlanDraft): void => {
      const atual = desejada.current;
      // Trocou de tipologia, ou alguém trocou a prancha, enquanto o arquivo vinha.
      if (atual?.id !== id) return;
      naMesa.current = id;
      const { metresPerPixel: _outra, ...semEscala } = pagina;
      // A escala guardada, convertida para a largura desta renderização (RN-247). É o que
      // devolve o traçado — em metros — para cima das paredes da folha.
      dispatch({
        type: 'SET_PLAN',
        plan:
          atual.calibration === undefined
            ? semEscala
            : { ...semEscala, metresPerPixel: metresPerPixelAt(atual.calibration, pagina.width) },
      });
    };

    const pronta = renderizadas.current.get(anexada.id);
    if (pronta !== undefined) {
      assentar(anexada.id, pronta);
      return;
    }
    // Já vindo: a releitura do registro que o `abrir` dispara não pode pedir o PDF de novo.
    if (emVoo.current === anexada.id) return;

    const id = anexada.id;
    emVoo.current = id;
    setAbrindo(true);
    setFalha(undefined);
    void (async () => {
      try {
        const url = await container.getFloorPlanSheetFile.execute(
          { projectId, sheetId: id },
          session,
        );
        if (!url.ok) {
          if (desejada.current?.id === id) setFalha(`A prancha não abriu: ${url.error.message}`);
          return;
        }
        const pagina = await renderizar(url.value);
        renderizadas.current.set(id, pagina);
        assentar(id, pagina);
      } catch {
        if (desejada.current?.id === id) {
          setFalha(
            'O arquivo da prancha chegou, mas não abriu nesta máquina. Tente recarregar a página.',
          );
        }
      } finally {
        if (emVoo.current === id) {
          emVoo.current = undefined;
          setAbrindo(false);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pranchas, anexada, pedida, projectId, userId]);

  // ── A prancha de antes, uma vez ─────────────────────────────────────────────
  useEffect(() => {
    if (migracaoFeita.current) return;
    if (pranchas === undefined || espinha === undefined) return;
    if (projectId === undefined || session === undefined) return;

    if (antiga.current === null) antiga.current = lerPranchaAntiga();
    const guardada = antiga.current;
    if (guardada === undefined) {
      migracaoFeita.current = true;
      return;
    }
    // Sem traçado nesta obra, a prancha pode ser de outra: espera a obra certa abrir.
    // O efeito volta a olhar quando o desenho chega do servidor.
    const alvo = alvoDaPranchaAntiga(workspace, espinha.positions);
    if (alvo === undefined) return;
    migracaoFeita.current = true;

    // O servidor já tem prancha para esse desenho: ela manda, e a de antes sai.
    if (pranchas.some((prancha) => prancha.floorPlanId === alvo)) {
      esquecerPranchaAntiga();
      return;
    }

    const nomeDoDesenho = espinha.floorPlans.find((plano) => plano.id === alvo)?.name;
    const tipo = /^data:([^;,]+)/.exec(guardada.image)?.[1] ?? 'image/jpeg';
    void container.attachFloorPlanSheet
      .execute(
        {
          projectId,
          floorPlanId: alvo,
          fileName: 'prancha-guardada-no-navegador.jpg',
          declaredMimeType: tipo,
          bytes: bytesDoDataUrl(guardada.image),
          source: 'NAVEGADOR',
          // A escala foi medida sobre esta mesma imagem, nesta mesma largura.
          ...(guardada.metresPerPixel === undefined
            ? {}
            : {
                calibration: {
                  metresPerPixel: guardada.metresPerPixel,
                  renderWidth: guardada.width,
                },
              }),
        },
        session,
      )
      .then(
        (resultado) => {
          if (resultado.ok) {
            esquecerPranchaAntiga();
            const { metresPerPixel: _escala, ...pagina } = guardada;
            renderizadas.current.set(resultado.value.id, pagina);
            setPranchas((atuais) => [
              ...(atuais ?? []).filter((prancha) => prancha.floorPlanId !== alvo),
              resultado.value,
            ]);
            toast.success(
              `A prancha que estava guardada neste navegador foi para a obra${
                nomeDoDesenho === undefined ? '' : `, no desenho ${nomeDoDesenho}`
              }.`,
            );
            return;
          }
          // Quem só vê o quantitativo não pode anexar — e não precisa ser avisado disso a
          // cada abertura. A prancha fica guardada para quem pode.
          if (resultado.error.kind === 'forbidden') return;
          toast.warning(
            `A prancha guardada neste navegador ainda não subiu: ${resultado.error.message} Ela continua guardada, e sobe na próxima abertura.`,
          );
        },
        () => {
          toast.warning(
            'A prancha guardada neste navegador ainda não subiu — sem conexão. Ela continua guardada, e sobe na próxima abertura.',
          );
        },
      );
  }, [pranchas, espinha, workspace, projectId, session, container]);

  // ── Os gestos ───────────────────────────────────────────────────────────────
  const abrir = useCallback(() => {
    setPedida(true);
    // Relê o registro: se outra pessoa trocou a prancha, é antes do "anexada por" que isso
    // precisa aparecer.
    setVersao((atual) => atual + 1);
  }, []);

  const anexar = useCallback(
    async (arquivo: File): Promise<ResultadoDaPrancha> => {
      if (projectId === undefined || session === undefined) {
        return { ok: false, erro: 'Abra uma obra antes de anexar a prancha.' };
      }
      if (desenhoId === undefined) {
        return {
          ok: false,
          erro: 'Esta tipologia ainda não está em nenhum desenho de pavimento. A prancha é do desenho: ponha a tipologia num pavimento antes de anexar.',
        };
      }

      let pagina: PlanDraft;
      try {
        pagina = await renderizar(arquivo);
      } catch {
        return {
          ok: false,
          erro: 'Não consegui abrir esse arquivo. Vale PDF ou imagem da prancha.',
        };
      }

      setEnviando(true);
      try {
        const comum = {
          projectId,
          floorPlanId: desenhoId,
          fileName: arquivo.name === '' ? 'prancha' : arquivo.name,
          ...(anexada === undefined ? {} : { replacesSheetId: anexada.id }),
        };
        // A página que o navegador acabou de renderizar, no lugar do original grande demais —
        // como JPEG que caiba no limite: a da tela é PNG e pode passar dele.
        const aRenderizada = async () => {
          const guardar = await guardarPagina(pagina, MAX_PLAN_SHEET_BYTES);
          return container.attachFloorPlanSheet.execute(
            {
              ...comum,
              declaredMimeType: guardar.mimeType,
              bytes: guardar.bytes,
              source: 'RENDERIZADA',
            },
            session,
          );
        };

        let aviso: string | undefined;
        let resultado: Awaited<ReturnType<typeof aRenderizada>>;
        if (arquivo.size > MAX_PLAN_SHEET_BYTES) {
          resultado = await aRenderizada();
          aviso = avisoDaRenderizada(arquivo.size, 'limite');
        } else {
          resultado = await container.attachFloorPlanSheet.execute(
            {
              ...comum,
              declaredMimeType: arquivo.type,
              bytes: new Uint8Array(await arquivo.arrayBuffer()),
              source: 'ORIGINAL',
            },
            session,
          );
          if (!resultado.ok && grandeDemais(resultado.error.code)) {
            resultado = await aRenderizada();
            aviso = avisoDaRenderizada(arquivo.size, 'caminho');
          }
        }

        if (!resultado.ok) {
          // Outra pessoa trocou a prancha enquanto esta tela estava aberta: relê, para o
          // próximo aviso já dizer quem.
          if (resultado.error.code === FLOOR_PLAN_SHEET_ERROR.REPLACED_MEANWHILE) {
            setVersao((atual) => atual + 1);
          }
          return { ok: false, erro: `A prancha não foi guardada: ${resultado.error.message}` };
        }

        const nova = resultado.value;
        const { metresPerPixel: _semEscala, ...semEscala } = pagina;
        renderizadas.current.set(nova.id, semEscala);
        setPranchas((atuais) => [
          ...(atuais ?? []).filter((prancha) => prancha.floorPlanId !== nova.floorPlanId),
          nova,
        ]);
        naMesa.current = nova.id;
        // Folha nova, sem escala: a planta pede a calibragem em seguida, como sempre pediu.
        dispatch({ type: 'SET_PLAN', plan: semEscala });
        return aviso === undefined ? { ok: true } : { ok: true, aviso };
      } finally {
        setEnviando(false);
      }
    },
    [container, session, projectId, desenhoId, anexada, renderizar, guardarPagina, dispatch],
  );

  const remover = useCallback(async (): Promise<ResultadoDaPrancha> => {
    if (projectId === undefined || session === undefined || anexada === undefined) {
      return { ok: true };
    }
    const resultado = await container.removeFloorPlanSheet.execute(
      { projectId, sheetId: anexada.id },
      session,
    );
    if (!resultado.ok) {
      if (resultado.error.code === FLOOR_PLAN_SHEET_ERROR.NOT_FOUND)
        setVersao((atual) => atual + 1);
      return { ok: false, erro: `A prancha não foi removida: ${resultado.error.message}` };
    }
    renderizadas.current.delete(anexada.id);
    setPranchas((atuais) => atuais?.filter((prancha) => prancha.id !== anexada.id));
    naMesa.current = null;
    // Só a folha sai. O traçado é geometria, está em metros, e fica (D23).
    dispatch({ type: 'CLEAR_PLAN' });
    return { ok: true };
  }, [container, session, projectId, anexada, dispatch]);

  const calibrar = useCallback(
    async (metresPerPixel: number): Promise<ResultadoDaPrancha> => {
      const pagina = bancada.current.plan;
      if (
        projectId === undefined ||
        session === undefined ||
        anexada === undefined ||
        desenhoId === undefined ||
        espinha === undefined ||
        pagina === undefined
      ) {
        return { ok: false, erro: 'Não há prancha guardada para calibrar.' };
      }

      // Primeiro o servidor, depois a mesa: a escala que só a tela conhecesse reescalaria o
      // traçado, e a próxima abertura traria a folha com a escala antiga por baixo dele.
      const resultado = await container.calibrateFloorPlanSheet.execute(
        { projectId, sheetId: anexada.id, metresPerPixel, renderWidth: pagina.width },
        session,
      );
      if (!resultado.ok) {
        return { ok: false, erro: `A escala não foi salva: ${resultado.error.message}` };
      }

      setPranchas((atuais) =>
        atuais?.map((prancha) => (prancha.id === resultado.value.id ? resultado.value : prancha)),
      );
      dispatch({
        type: 'SET_PLAN_SCALE',
        metresPerPixel,
        unitTypeIds: tipologiasDoDesenho(espinha.positions, desenhoId),
      });
      return { ok: true };
    },
    [container, session, projectId, anexada, desenhoId, espinha, dispatch],
  );

  return {
    ...(desenhoId === undefined ? {} : { desenhoId }),
    ...(anexada === undefined ? {} : { anexada }),
    carregando: pranchas === undefined && falha === undefined,
    abrindo,
    enviando,
    ...(falha === undefined ? {} : { falha }),
    abrir,
    anexar,
    remover,
    calibrar,
  };
}
